import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { APP_CONFIG } from '../../config/appConfig';
import { toE164KR, genOtp6, sendOtpViaProxy, findUidByPhoneE164, resetPasswordViaProxy } from '../../services/recoveryService';

function normalizePhone(raw) {
  return raw.replace(/[^0-9]/g, "");
}

function isTestNumber(phone) {
  const n = normalizePhone(phone);
  const { start, end } = APP_CONFIG.testRange;
  return n >= start && n <= end;
}

const FindPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // phone | otp | reset | done
  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [testCode, setTestCode] = useState('');
  const [foundUid, setFoundUid] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const startTimer = () => {
    setTimer(180);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError('올바른 전화번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    if (isTestNumber(normalized)) {
      const otp = genOtp6();
      setTestCode(otp);
      setServerCode(otp);
      setCodeSent(true);
      setStep('otp');
      startTimer();
      setBusy(false);
      return;
    }

    const otp = genOtp6();
    setServerCode(otp);

    try {
      await sendOtpViaProxy({ phone: normalized, authcode: otp });
      setCodeSent(true);
      setStep('otp');
      startTimer();
    } catch (err) {
      setError(err.message || 'SMS 발송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!code.trim()) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    if (serverCode && code.trim() !== serverCode) {
      setError('인증번호가 일치하지 않습니다.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      const phoneE164 = toE164KR(phone);
      const uid = await findUidByPhoneE164(phoneE164);
      if (!uid) {
        setError('해당 전화번호로 등록된 계정이 없습니다.');
        setBusy(false);
        return;
      }
      setFoundUid(uid);
      setStep('reset');
    } catch (err) {
      console.error('계정 찾기 실패:', err);
      setError('계정 찾기에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPw || newPw.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      await resetPasswordViaProxy({ uid: foundUid, newPassword: newPw });
      setStep('done');
    } catch (err) {
      console.error('비밀번호 변경 실패:', err);
      setError(err.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (step === 'done') {
    return (
      <Container>
        <Title>비밀번호 변경 완료</Title>
        <FormArea>
          <SuccessText>새로운 비밀번호로 로그인해주세요.</SuccessText>
          <PrimaryBtn onClick={() => navigate('/login', { replace: true })}>
            로그인하러 가기
          </PrimaryBtn>
        </FormArea>
      </Container>
    );
  }

  if (step === 'reset') {
    return (
      <Container>
        <Title>새 비밀번호 설정</Title>
        <Desc>새 비밀번호를 입력해주세요.</Desc>

        <FormArea>
          <Label>새 비밀번호</Label>
          <Input
            type="password"
            placeholder=""
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />

          <Label>비밀번호 확인</Label>
          <Input
            type="password"
            placeholder=""
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />

          {error && <ErrorText>{error}</ErrorText>}

          <PrimaryBtn onClick={handleResetPassword} disabled={busy}>
            {busy ? '변경 중...' : '비밀번호 변경'}
          </PrimaryBtn>
        </FormArea>
      </Container>
    );
  }

  return (
    <Container>
      <Title>비밀번호 찾기</Title>
      <Desc>전화번호로 비밀번호를 변경할 수 있습니다.</Desc>

      <FormArea>
        <Label>전화번호</Label>
        <Input
          type="tel"
          placeholder=""
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={codeSent}
        />
        <SendBtn onClick={handleSendCode} disabled={busy || (codeSent && timer > 0)}>
          {codeSent ? '재전송' : '인증번호 전송'}
        </SendBtn>

        {step === 'otp' && (
          <>
            <Label>인증번호 {timer > 0 && <TimerText>{formatTimer(timer)}</TimerText>}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder=""
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {testCode && <TestCodeBox>테스트 코드: {testCode}</TestCodeBox>}
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        {step === 'otp' && (
          <PrimaryBtn onClick={handleVerifyOtp} disabled={busy}>
            {busy ? '확인 중...' : '인증 확인'}
          </PrimaryBtn>
        )}

        <LinkBtn onClick={() => navigate('/login')}>
          로그인으로 돌아가기
        </LinkBtn>
      </FormArea>
    </Container>
  );
};

export default FindPasswordPage;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #EEF2FF 0%, #F5F7FA 40%);
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 22px;
  text-align: center;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Desc = styled.p`
  font-size: 15px;
  color: #888;
  text-align: center;
  margin-bottom: 40px;
`;

const FormArea = styled.div`
  width: 100%;
  max-width: 480px;
`;

const SuccessText = styled.p`
  font-size: 16px;
  color: #4A7CFE;
  text-align: center;
  margin: 32px 0;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #333;
`;

const TimerText = styled.span`
  color: #4A7CFE;
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  &:focus { border-color: #4A7CFE; outline: none; }
  &:disabled { background: #f5f5f5; }
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 12px;
  border-radius: 10px;
  background: #4A7CFE;
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: 28px;
  background: #4A7CFE;
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const LinkBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const TestCodeBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
  font-size: 14px;
  color: #E65100;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 14px;
  margin-top: 10px;
`;
